import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { useDashboardData } from '../../hooks/admin/useDashboardData';
import RevenueChart from '../../components/admin/charts/RevenueChart';
import OrderStatusChart from '../../components/admin/charts/OrderStatusChart';
import UserRegistrationChart from '../../components/admin/charts/UserRegistrationChart';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  getTimeRangeFromString,
  formatDate,
  formatRelativeTime,
  getActivityIcon,
} from '../../utils/dashboardUtils';
import AiHealthWidget from './AiHealthWidget';
import type { DashboardOverview } from '../../types/admin';

import { INK, FONTS, primaryBtnSx, ghostBtnSx } from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  KPIStrip,
  KPI,
  LedgerCard,
  SectionHeader,
  ledgerTableHeadSx,
  ledgerRowSx,
  ledgerMenuPaperSx,
  HeaderIconBtn,
  StatusDot,
  MonoTag,
} from '../../components/admin/LedgerPrimitives';

const orderStatusTone = (status: string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  const s = (status || '').toLowerCase();
  if (s === 'delivered' || s === 'completed') return 'active';
  if (s === 'cancelled' || s === 'failed') return 'inactive';
  if (s === 'pending' || s === 'draft') return 'warning';
  if (s === 'shipped' || s === 'processing' || s === 'created') return 'info';
  return 'neutral';
};

const DashboardPage: React.FC = () => {
  const [dateRange, setDateRange] = useState('This month');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const timeRange = getTimeRangeFromString(dateRange);
  const {
    overview,
    userAnalytics,
    orderAnalytics,
    financialReports,
    geographicAnalytics,
    recentActivities,
    recentOrders,
    isLoading,
    isError,
    refetchAll,
  } = useDashboardData(timeRange);

  if (isError) {
    return (
      <LedgerPage>
        <Alert
          severity="error"
          sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans }}
          action={
            <Button onClick={refetchAll} sx={{ ...ghostBtnSx, py: 0.5, px: 1.5, fontSize: 12 }}>
              Retry
            </Button>
          }
        >
          Failed to load dashboard data.
        </Alert>
      </LedgerPage>
    );
  }

  const data = overview.data?.overview as DashboardOverview | undefined;

  return (
    <LedgerPage eyebrowFooter="Almaship · Operations dashboard">
      <PageHeader
        eyebrow="Admin · Overview"
        title={
          <>
            Operations
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              {' '}at a glance.
            </Box>
          </>
        }
        description="A live snapshot of users, orders, revenue, and AI health across the platform."
        actions={
          <>
            <Button
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              startIcon={<DateRangeIcon sx={{ fontSize: 16 }} />}
              sx={ghostBtnSx}
            >
              {dateRange}
            </Button>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
              PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: 160 } }}
            >
              {['Today', 'Yesterday', 'This week', 'This month', 'Last 3 months', 'This year'].map((r) => (
                <MenuItem key={r} onClick={() => { setDateRange(r); setMenuAnchorEl(null); }}>
                  {r}
                </MenuItem>
              ))}
            </Menu>
            <HeaderIconBtn title="Refresh" onClick={refetchAll}>
              {isLoading ? <CircularProgress size={16} sx={{ color: INK.muted }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </HeaderIconBtn>
            <Button sx={primaryBtnSx} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}>
              Generate report
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <KPIStrip cols={4}>
        <KPI
          label="Total users"
          value={isLoading || !data ? '—' : formatNumber(data.total_users)}
          sub={data ? `${formatPercentage(data.user_growth_percentage)} vs last period` : ''}
          accent={!!(data && data.user_growth_percentage >= 0)}
        />
        <KPI
          label="Total orders"
          value={isLoading || !data ? '—' : formatNumber(data.total_orders)}
          sub={data ? `${formatPercentage(data.order_growth_percentage)} vs last period` : ''}
        />
        <KPI
          label="Revenue"
          value={isLoading || !data ? '—' : formatCurrency(data.total_revenue)}
          sub={data ? `${formatPercentage(data.order_growth_percentage)} vs last period` : ''}
        />
        <KPI
          label="Wallet balance"
          value={isLoading || !data ? '—' : formatCurrency(data.total_wallet_balance)}
          sub="total platform"
        />
      </KPIStrip>

      {/* Charts row 1 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <LedgerCard>
          <RevenueChart
            data={financialReports.data?.financial_reports.revenue_trends || []}
            isLoading={financialReports.isLoading}
          />
        </LedgerCard>
        <LedgerCard>
          <OrderStatusChart
            data={
              orderAnalytics.data?.order_analytics.order_status_distribution || {
                draft: 0,
                created: 0,
                processing: 0,
                completed: 0,
                cancelled: 0,
                failed: 0,
              }
            }
            isLoading={orderAnalytics.isLoading}
          />
        </LedgerCard>
      </Box>

      {/* AI Health */}
      <Box sx={{ mb: 3 }}>
        <LedgerCard>
          <AiHealthWidget />
        </LedgerCard>
      </Box>

      {/* Charts row 2 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <LedgerCard>
          <UserRegistrationChart
            data={userAnalytics.data?.user_analytics.registration_trends || []}
            isLoading={userAnalytics.isLoading}
          />
        </LedgerCard>
        <LedgerCard>
          <SectionHeader eyebrow="Geography" title="Top countries" description="Where users come from" />
          {geographicAnalytics.isLoading ? (
            <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: INK.accent }} />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {geographicAnalytics.data?.geographic_analytics.users_by_country?.slice(0, 5).map((country, i) => {
                const total = geographicAnalytics.data?.geographic_analytics.users_by_country?.reduce(
                  (sum, c) => sum + c.user_count,
                  0
                ) || 1;
                const pct = (country.user_count / total) * 100;
                return (
                  <Box key={country.iso2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1.5}>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.04em' }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                          {country.name}
                        </Typography>
                        <MonoTag>{country.iso2}</MonoTag>
                      </Stack>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text, fontWeight: 600 }}>
                        {formatNumber(country.user_count)}
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 2, bgcolor: INK.hairlineSoft, overflow: 'hidden' }}>
                      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: i === 0 ? INK.accent : INK.muted, transition: 'width 0.5s ease' }} />
                    </Box>
                  </Box>
                );
              }) || (
                <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.subtle, textAlign: 'center', py: 4 }}>
                  No geographic data
                </Typography>
              )}
            </Stack>
          )}
        </LedgerCard>
      </Box>

      {/* Recent orders */}
      <LedgerCard padded={false} mb={3}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
          <SectionHeader
            eyebrow="Activity"
            title="Recent orders"
            description="Most recent ledger entries"
            action={
              <Stack direction="row" spacing={1}>
                <HeaderIconBtn title="Refresh" onClick={() => recentOrders.refetch()}>
                  {recentOrders.isLoading ? (
                    <CircularProgress size={14} sx={{ color: INK.muted }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  )}
                </HeaderIconBtn>
                <Button component={Link} to="/admin/orders" sx={ghostBtnSx}>
                  View all
                </Button>
              </Stack>
            }
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={ledgerTableHeadSx}>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentOrders.isLoading
                ? [...Array(5)].map((_, i) => (
                    <TableRow key={i} sx={ledgerRowSx}>
                      {[...Array(6)].map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton sx={{ bgcolor: INK.hairlineSoft }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : recentOrders.data?.data?.length
                ? recentOrders.data.data.map((order) => (
                    <TableRow key={order.id} sx={ledgerRowSx}>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 600 }}>
                          {order.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: INK.text }}>
                        {order.user.first_name} {order.user.last_name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
                        {formatDate(order.created_at)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 600 }}>
                        {formatCurrency(order.total_price, order.currency)}
                      </TableCell>
                      <TableCell>
                        <MonoTag>{order.carrier || '—'}</MonoTag>
                      </TableCell>
                      <TableCell>
                        <StatusDot label={(order.order_status || '').replace(/_/g, ' ')} tone={orderStatusTone(order.order_status)} />
                      </TableCell>
                    </TableRow>
                  ))
                : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ borderBottom: 'none', py: 6, textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.subtle, letterSpacing: '-0.01em' }}>
                        No recent orders.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </LedgerCard>

      {/* Bottom row: activity + carriers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <LedgerCard>
          <SectionHeader
            eyebrow="Log"
            title="Recent activity"
            action={
              <HeaderIconBtn title="Refresh" onClick={() => recentActivities.refetch()}>
                {recentActivities.isLoading ? (
                  <CircularProgress size={14} sx={{ color: INK.muted }} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                )}
              </HeaderIconBtn>
            }
          />
          {recentActivities.isLoading ? (
            <Stack spacing={2}>
              {[...Array(4)].map((_, i) => (
                <Box key={i}>
                  <Skeleton width="80%" sx={{ bgcolor: INK.hairlineSoft }} />
                  <Skeleton width="50%" sx={{ bgcolor: INK.hairlineSoft }} />
                </Box>
              ))}
            </Stack>
          ) : recentActivities.data?.recent_activities?.length ? (
            <Stack spacing={0}>
              {recentActivities.data.recent_activities.slice(0, 8).map((activity, i, arr) => (
                <Box
                  key={i}
                  sx={{
                    position: 'relative',
                    pl: 4,
                    py: 1.5,
                    borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${INK.hairlineSoft}`,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 12,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      bgcolor: i === arr.length - 1 ? 'transparent' : INK.hairline,
                    },
                    '&::after': {
                      content: `"${getActivityIcon(activity.type)}"`,
                      position: 'absolute',
                      left: 4,
                      top: 12,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: INK.surface,
                      border: `1px solid ${INK.hairline}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                    },
                  }}
                >
                  <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text, fontWeight: 500, lineHeight: 1.4 }}>
                    {activity.message}
                  </Typography>
                  <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, mt: 0.25, letterSpacing: '0.04em' }}>
                    {formatRelativeTime(activity.timestamp)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.subtle, textAlign: 'center', py: 4 }}>
              No activity.
            </Typography>
          )}
        </LedgerCard>

        <LedgerCard>
          <SectionHeader eyebrow="Volume" title="Top carriers" description="By order count" />
          {orderAnalytics.isLoading ? (
            <Stack spacing={2}>
              {[...Array(4)].map((_, i) => (
                <Box key={i}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Skeleton width="40%" sx={{ bgcolor: INK.hairlineSoft }} />
                    <Skeleton width="20%" sx={{ bgcolor: INK.hairlineSoft }} />
                  </Stack>
                  <Skeleton height={4} sx={{ bgcolor: INK.hairlineSoft }} />
                </Box>
              ))}
            </Stack>
          ) : orderAnalytics.data?.order_analytics.top_carriers?.length ? (
            <Stack spacing={1.75}>
              {orderAnalytics.data.order_analytics.top_carriers.slice(0, 5).map((carrier, i) => {
                const total =
                  orderAnalytics.data?.order_analytics.top_carriers.reduce((sum, c) => sum + c.count, 0) || 1;
                const pct = (carrier.count / total) * 100;
                return (
                  <Box key={carrier.carrier}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1.5}>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.04em' }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                          {carrier.carrier}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text, fontWeight: 600 }}>
                        {carrier.count}
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 2, bgcolor: INK.hairlineSoft, overflow: 'hidden' }}>
                      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: i === 0 ? INK.accent : INK.muted, transition: 'width 0.5s ease' }} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.subtle, textAlign: 'center', py: 4 }}>
              No carrier data.
            </Typography>
          )}
        </LedgerCard>
      </Box>
    </LedgerPage>
  );
};

export default DashboardPage;
